// frontend/src/marketing/SiteBanner.jsx
import React, { useEffect, useState } from "react";

/** Font-size enum → safe CSS rem (mirrors the backend's allowed values). */
const FONT_REM = { s: "0.8125rem", m: "0.9375rem", l: "1.125rem", xl: "1.375rem" };

const DISMISS_KEY = "qrflow.banner.dismissed";

/**
 * SiteBanner — the public announcement band.
 *
 * Self-fetches the single active banner from the unauthenticated
 * `/api/public/banner` endpoint and renders it at the very top of the public
 * shell. Colours and font size come from the operator-configured banner; the
 * message is rendered as plain text (never HTML). A dismissible banner remembers
 * its dismissal per banner id in localStorage so it doesn't re-nag.
 */
export default function SiteBanner() {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("/api/public/banner")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setBanner(data);
        // Hidden only if this exact banner id was dismissed before.
        if (
          data.dismissible &&
          window.localStorage.getItem(DISMISS_KEY) === data.id
        ) {
          setDismissed(true);
        }
      })
      .catch(() => {
        /* a failed banner fetch must never break the public site */
      });
    return () => {
      active = false;
    };
  }, []);

  if (!banner || dismissed) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, banner.id);
    } catch {
      /* private mode — fall back to in-memory dismissal */
    }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Site announcement"
      style={{ background: banner.bg_color, color: banner.text_color }}
      className="w-full"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 py-2 text-center">
        <p
          className="font-medium"
          style={{ fontSize: FONT_REM[banner.font_size] || FONT_REM.m }}
        >
          {banner.message}
          {banner.link_url && (
            <a
              href={banner.link_url}
              className="ml-2 underline underline-offset-2 hover:opacity-80"
              style={{ color: banner.text_color }}
            >
              {banner.link_label || "Learn more"}
            </a>
          )}
        </p>
        {banner.dismissible && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss announcement"
            className="ml-1 shrink-0 rounded px-1 text-lg leading-none opacity-70 hover:opacity-100"
            style={{ color: banner.text_color }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
